import { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import { BinLocationCard } from 'views/warehouse/components/BinLocationCard';
import { MoveWarningData } from 'views/warehouse/MoveWarningContext/types';
import { StockLotRequestCard } from 'views/warehouse/MoveWarningContext/components/StockLotDisplay/StockLotRequestCard';
import { MismatchReason } from 'views/warehouse/utils';

type AssignmentMismatchContentProps = {
  warningData: MoveWarningData;
};

export const AssignmentMismatchContent: FC<AssignmentMismatchContentProps> = ({
  warningData,
}) => {
  const theme = useTheme();

  const getMismatchDescription = () => {
    const reasons = warningData.mismatchReasons || [];
    const hasPurposeMismatch = reasons.includes(MismatchReason.PURPOSE);
    const hasKitMismatch = reasons.includes(MismatchReason.KIT_REQUEST);

    if (hasPurposeMismatch && hasKitMismatch) {
      return 'This stock lot has both a purpose mismatch and kit request mismatch with the destination location.';
    }
    if (hasPurposeMismatch) {
      return "This stock lot's handling request purpose doesn't match the destination location purpose.";
    }
    if (hasKitMismatch) {
      return "This stock lot's kit request assignment doesn't match the destination location.";
    }

    return "This stock lot has a handling request that doesn't match the destination location.";
  };

  const getResolutionText = () => {
    const reasons = warningData.mismatchReasons || [];
    const hasPurposeMismatch = reasons.includes(MismatchReason.PURPOSE);
    const hasKitMismatch = reasons.includes(MismatchReason.KIT_REQUEST);

    if (hasPurposeMismatch && hasKitMismatch) {
      return 'Scan a location with the correct purpose and kit request assignment, or return the stock lot to its original location';
    }
    if (hasPurposeMismatch) {
      return 'Scan a location with the correct purpose or return the stock lot to its original location';
    }
    if (hasKitMismatch) {
      return 'Scan a location with the correct kit request assignment or return the stock lot to its original location';
    }

    return 'Scan a location that matches the handling request purpose or return the stock lot to its original location';
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Required Action:
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {getMismatchDescription()}
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <StockLotRequestCard warningData={warningData} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconX
            size={32}
            color={theme.palette.error.main}
            style={{ marginBottom: '4px' }}
          />
          <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
            Mismatch
          </Typography>
        </Box>

        <BinLocationCard stockLocation={warningData.destination} />
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 2, textAlign: 'center' }}
      >
        {getResolutionText()}
      </Typography>
    </Box>
  );
};
